import { useMemo } from 'react'
import { useAtom } from 'jotai'
import { useLocation } from 'react-router'
import { Moon, Sun } from 'lucide-react'
import { useAuth } from '../contexts/AuthContext'
import { useWorkspace } from '../contexts/WorkspaceContext'
import { isDarkAtom } from '../state/store'
import { saveTheme } from '../lib/storage'
import { Tooltip, TooltipContent, TooltipTrigger } from './ui/tooltip'

const PAGE_TITLES: Record<string, string> = {
  '/admin/my-classes': 'My Classes',
  '/admin/usage': 'Usage',
  '/admin/flagged': 'Flagged & Safety',
  '/admin/students': 'Students',
  '/admin/teachers': 'Teachers',
  '/admin/workspaces': 'Workspaces',
}

export function AdminTopBar() {
  const { pathname } = useLocation()
  const { user } = useAuth()
  const { activeWorkspace } = useWorkspace()
  const [isDark, setIsDark] = useAtom(isDarkAtom)

  const pageTitle = useMemo(() => {
    if (pathname.startsWith('/admin/my-classes/')) return 'My Classes'
    if (pathname.startsWith('/admin/class-settings/')) return 'My Classes'
    return PAGE_TITLES[pathname] ?? 'Admin'
  }, [pathname])

  const resolvedName = user?.name || user?.email || 'User'
  const resolvedAvatar = user?.picture ?? user?.avatar
  const userInitial = resolvedName.charAt(0).toUpperCase()

  const toggleTheme = () => {
    setIsDark((prev) => {
      const next = !prev
      document.documentElement.classList.toggle('dark', next)
      saveTheme(next ? 'dark' : 'light')
      return next
    })
  }

  return (
    <header className="shrink-0 border-b-2 border-nb-border bg-secondary-bg px-6 py-3">
      <div className="flex items-center justify-between gap-4">
        <h1 className="font-heading text-lg text-foreground">{pageTitle}</h1>

        <div className="flex items-center gap-3">
          {activeWorkspace ? (
            <div className="hidden max-w-[220px] truncate rounded-base border-2 border-nb-border bg-background px-3 py-1.5 text-sm font-heading sm:block">
              {activeWorkspace.name}
            </div>
          ) : null}

          <Tooltip>
            <TooltipTrigger asChild>
              <button
                className="rounded-base border-2 border-nb-border p-1.5 text-foreground transition-all hover:bg-main/20 active:translate-x-boxShadowX active:translate-y-boxShadowY"
                onClick={toggleTheme}
              >
                {isDark ? <Sun className="h-5 w-5" /> : <Moon className="h-5 w-5" />}
              </button>
            </TooltipTrigger>
            <TooltipContent side="bottom">{isDark ? 'Light mode' : 'Dark mode'}</TooltipContent>
          </Tooltip>

          {resolvedAvatar ? (
            <img
              src={resolvedAvatar}
              alt={resolvedName}
              className="size-10 rounded-full border-2 border-nb-border object-cover"
            />
          ) : (
            <div className="flex size-10 items-center justify-center rounded-full border-2 border-nb-border bg-main text-sm font-heading text-main-foreground">
              {userInitial}
            </div>
          )}
        </div>
      </div>
    </header>
  )
}
